import React, { useState, useEffect, useRef } from 'react';
import { 
  Stack, 
  Text, 
  TagsInput,
  Transition,
  Badge,
  Group,
  Tooltip
} from '@mantine/core';

interface KeywordsSectionProps {
  keywords: string[];
  eventKeywords?: string[];
  onSave: (keywords: string[]) => void;
}

export const KeywordsSection: React.FC<KeywordsSectionProps> = ({ 
  keywords: initialKeywords, 
  eventKeywords = [],
  onSave 
}) => {
  // Combine and deduplicate keywords from both sources
  const [keywords, setKeywords] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pulseEffect, setPulseEffect] = useState(false);
  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousKeywordsRef = useRef<string[]>([]);
  
  // Get unique keywords from both image and event
  useEffect(() => {
    // First add event keywords
    const uniqueKeywords: string[] = [];
    const seenKeywords = new Set<string>();
    
    // Add event keywords first
    for (const keyword of eventKeywords || []) {
      const lowerCaseKeyword = keyword.toLowerCase();
      if (!seenKeywords.has(lowerCaseKeyword)) {
        seenKeywords.add(lowerCaseKeyword);
        uniqueKeywords.push(keyword);
      }
    }
    
    // Then add image keywords
    for (const keyword of initialKeywords || []) {
      const lowerCaseKeyword = keyword.toLowerCase();
      if (!seenKeywords.has(lowerCaseKeyword)) {
        seenKeywords.add(lowerCaseKeyword);
        uniqueKeywords.push(keyword);
      }
    }
    
    setKeywords(uniqueKeywords);
    previousKeywordsRef.current = uniqueKeywords;
  }, [initialKeywords, eventKeywords]);
  
  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, []);
  
  // Show error message for 6 seconds then hide it
  const showErrorMessage = (message: string) => {
    // If the message is already showing, trigger pulse effect
    if (errorMessage) {
      // Trigger pulse effect
      setPulseEffect(true);
      
      // Reset pulse effect after a short delay
      setTimeout(() => {
        setPulseEffect(false);
      }, 300);
      
      // Reset the error timeout
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      
      errorTimeoutRef.current = setTimeout(() => {
        setErrorMessage(null);
      }, 6000);
    } else {
      // Set the error message
      setErrorMessage(message);
      
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      
      errorTimeoutRef.current = setTimeout(() => {
        setErrorMessage(null);
      }, 6000);
    }
  };

  return (
    <Stack gap={0} style={{flex: 1}}>
      <Text fw={500} size={'sm'}>Keywords</Text>
      <TagsInput
        mt={5}
        placeholder="Type and press enter"
        value={keywords}
        onChange={(newValues) => {
          // Reset error message if input is being cleared
          if (newValues.length === 0 && keywords.length > 0) {
            setErrorMessage(null);
            setKeywords([]);
            onSave([]);
            return;
          }
          
          // Check if this is just a re-render of the same keywords (happens when TagsInput refreshes)
          // Convert both arrays to lowercase for case-insensitive comparison
          const prevKeywordsLower = previousKeywordsRef.current.map(k => k.toLowerCase());
          const newValuesLower = newValues.map(k => k.toLowerCase());
          
          // Sort both arrays for consistent comparison
          prevKeywordsLower.sort();
          newValuesLower.sort();
          
          // If the arrays are the same (just in different order or case), do nothing
          if (prevKeywordsLower.length === newValuesLower.length && 
              prevKeywordsLower.every((val, idx) => val === newValuesLower[idx])) {
            return; // No actual change, just a re-render
          }
          
          // Check for duplicates only within the new values
          const hasDuplicatesInNewValues = new Set(newValuesLower).size !== newValuesLower.length;
          
          if (hasDuplicatesInNewValues) {
            showErrorMessage('Duplicate keywords detected');
            return; // Don't proceed with saving duplicates
          }
          
          // Identify what's actually new (not in previous keywords)
          const prevKeywordsLowerSet = new Set(prevKeywordsLower);
          const actuallyNewKeywords = newValues.filter(k => 
            !prevKeywordsLowerSet.has(k.toLowerCase())
          );
          
          // Convert event keywords to lowercase for comparison
          const eventKeywordsLower = (eventKeywords || []).map(k => k.toLowerCase());
          const eventKeywordsLowerSet = new Set(eventKeywordsLower);
          
          // Check if any of the new keywords conflict with event keywords
          const conflictsWithEvent = actuallyNewKeywords.some(k => 
            eventKeywordsLowerSet.has(k.toLowerCase())
          );
          
          if (conflictsWithEvent) {
            showErrorMessage('This keyword is already included in the event keywords');
            return;
          }
          
          // If we got here, the keywords are valid to add
          setErrorMessage(null); // Clear any existing error
          
          // Combine keywords with event keywords first, then user values (preserving case)
          const uniqueKeywords = [];
          const seenKeywords = new Set<string>();
          
          // First add event keywords
          for (const keyword of eventKeywords || []) {
            const lowerCaseKeyword = keyword.toLowerCase();
            seenKeywords.add(lowerCaseKeyword);
            uniqueKeywords.push(keyword);
          }
          
          // Then add user-entered keywords
          for (const keyword of newValues) {
            const lowerCaseKeyword = keyword.toLowerCase();
            if (!seenKeywords.has(lowerCaseKeyword)) {
              seenKeywords.add(lowerCaseKeyword);
              uniqueKeywords.push(keyword);
            }
          }
          
          // Update state and refs
          setKeywords(uniqueKeywords);
          previousKeywordsRef.current = uniqueKeywords;
          
          // Only save image-specific keywords (exclude event keywords)
          // Use case-insensitive comparison to properly filter out event keywords
          const imageKeywords = uniqueKeywords.filter(keyword => 
            !eventKeywordsLower.includes(keyword.toLowerCase())
          );
          
          onSave(imageKeywords);
        }}
        size="sm"
        allowDuplicates
      />
      <Transition
        mounted={!!errorMessage}
        transition="fade"
        duration={400}
        timingFunction="ease-in-out"
      >
        {(styles) => (
          <Text 
            size="xs" 
            fs="italic" 
            c="red" 
            mt={5} 
            style={{
              ...styles,
              transform: pulseEffect ? 'scale(1.1)' : 'scale(1)',
              transitionProperty: 'transform',
              transitionDuration: '300ms',
              transitionTimingFunction: 'ease-in-out'
            }}
          >
            {errorMessage}
          </Text>
        )}
      </Transition>
    </Stack>
  );
};
